import { useMemo } from 'react';
import { Configuration, ProductListsApi } from '@/api';
import {
  invalidateProductListDetail,
  invalidateProductListLists,
  invalidateProductListPortfolios,
  productListQueryKeys,
} from '@/api/queries/productListQueryKeys';
import { ProductListCreate, ProductListUpdate } from '@/models/ProductList';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ProductListUpdateVariables {
  productListId: string;
  productList: ProductListUpdate;
}

interface ProductListIdsVariables {
  productListId: string;
  ids: string[];
}

type BatchOperation = 'add' | 'remove';
type AssignmentOperation = 'assign' | 'unassign';

interface ProductListBatchVariables extends ProductListIdsVariables {
  operation: BatchOperation;
}

interface ProductListAssignmentVariables extends ProductListIdsVariables {
  operation: AssignmentOperation;
}

const useProductListMutations = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const productListsApi = useMemo(() => new ProductListsApi(new Configuration(auth)), [auth]);

  const createMutation = useMutation({
    mutationFn: (productList: ProductListCreate) => productListsApi.createProductList(productList),
    onSuccess: () => invalidateProductListLists(queryClient),
  });

  const updateMutation = useMutation({
    mutationFn: ({ productListId, productList }: ProductListUpdateVariables) =>
      productListsApi.updateProductList(productListId, productList),
    onSuccess: async (_, { productListId }) => {
      await Promise.all([
        invalidateProductListLists(queryClient),
        invalidateProductListDetail(queryClient, productListId),
        invalidateProductListPortfolios(queryClient),
      ]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (productListId: string) => productListsApi.deleteProductList(productListId),
    onSuccess: async (_, productListId) => {
      await Promise.all([
        invalidateProductListLists(queryClient),
        invalidateProductListDetail(queryClient, productListId),
        invalidateProductListPortfolios(queryClient),
      ]);
      queryClient.removeQueries({ queryKey: productListQueryKeys.detail(productListId) });
    },
  });

  const skuMembershipMutation = useMutation({
    mutationFn: ({ productListId, ids, operation }: ProductListBatchVariables) =>
      operation === 'add'
        ? productListsApi.addSkusToProductList(productListId, ids)
        : productListsApi.removeSkusFromProductList(productListId, ids),
    onSuccess: (_, { productListId }) =>
      queryClient.invalidateQueries({
        queryKey: productListQueryKeys.skuIdsRoot(productListId),
      }),
  });

  const retailerAssignmentMutation = useMutation({
    mutationFn: ({ productListId, ids, operation }: ProductListAssignmentVariables) =>
      operation === 'assign'
        ? productListsApi.assignRetailersToProductList(productListId, ids)
        : productListsApi.unassignRetailersFromProductList(productListId, ids),
    onSuccess: async (_, { productListId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: productListQueryKeys.retailerAssignmentsRoot(productListId),
        }),
        queryClient.invalidateQueries({
          queryKey: productListQueryKeys.retailerAssignmentOptions(productListId),
        }),
        invalidateProductListPortfolios(queryClient),
      ]);
    },
  });

  const storeAssignmentMutation = useMutation({
    mutationFn: ({ productListId, ids, operation }: ProductListAssignmentVariables) =>
      operation === 'assign'
        ? productListsApi.assignStoresToProductList(productListId, ids)
        : productListsApi.unassignStoresFromProductList(productListId, ids),
    onSuccess: async (_, { productListId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: productListQueryKeys.storeAssignmentsRoot(productListId),
        }),
        queryClient.invalidateQueries({
          queryKey: productListQueryKeys.storeAssignmentOptions(productListId),
        }),
        invalidateProductListPortfolios(queryClient),
      ]);
    },
  });

  return {
    createProductList: createMutation.mutateAsync,
    updateProductList: updateMutation.mutateAsync,
    deleteProductList: deleteMutation.mutateAsync,
    addSkusToProductList: (variables: ProductListIdsVariables) =>
      skuMembershipMutation.mutateAsync({ ...variables, operation: 'add' }),
    removeSkusFromProductList: (variables: ProductListIdsVariables) =>
      skuMembershipMutation.mutateAsync({ ...variables, operation: 'remove' }),
    assignRetailersToProductList: (variables: ProductListIdsVariables) =>
      retailerAssignmentMutation.mutateAsync({ ...variables, operation: 'assign' }),
    unassignRetailersFromProductList: (variables: ProductListIdsVariables) =>
      retailerAssignmentMutation.mutateAsync({ ...variables, operation: 'unassign' }),
    assignStoresToProductList: (variables: ProductListIdsVariables) =>
      storeAssignmentMutation.mutateAsync({ ...variables, operation: 'assign' }),
    unassignStoresFromProductList: (variables: ProductListIdsVariables) =>
      storeAssignmentMutation.mutateAsync({ ...variables, operation: 'unassign' }),
    isCreatingProductList: createMutation.isPending,
    isUpdatingProductList: updateMutation.isPending,
    isDeletingProductList: deleteMutation.isPending,
    isUpdatingSkuMembership: skuMembershipMutation.isPending,
    isUpdatingRetailerAssignments: retailerAssignmentMutation.isPending,
    isUpdatingStoreAssignments: storeAssignmentMutation.isPending,
    error:
      createMutation.error ??
      updateMutation.error ??
      deleteMutation.error ??
      skuMembershipMutation.error ??
      retailerAssignmentMutation.error ??
      storeAssignmentMutation.error,
  };
};

export default useProductListMutations;
